import {
  useMutation,
  useQuery,
  useQueryClient,
  type MutationObserverOptions,
} from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { useArrayFilter } from '@vueuse/core';
import { mergeMutationOptions, mergeQueryOptions, type QueryOptions } from '@/modules/query/utils';
import type {
  OwnProduct,
  Product,
  CreateProductRequest,
  EditProductRequest,
} from '@/types/models/products';
import { useMode } from '@/modules/mode/composables';
import api, {
  type SetSupplierAssociatedProductsRequest,
  type SetClientAssociatedProductsRequest,
} from './api';
import {
  OWN_PRODUCTS_QUERY,
  ORGANISATION_PRODUCTS_QUERY,
  SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY,
  CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY,
} from './queryKeys';

export function useOwnProducts(options?: QueryOptions) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [OWN_PRODUCTS_QUERY],
      async queryFn() {
        const res = await api.getOwnProducts();
        return res.data;
      },
    }),
  );

  const ownProducts = computed(
    () =>
      query.data.value
        ?.map<OwnProduct>((product) => ({
          ...product,
          answerIDs: product.answerIDs ?? [], // Work around backend issue where field can be null
          clientIDs: product.clientIDs ?? [],
        }))
        .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)) ?? [],
  );
  const archivedOwnProducts = useArrayFilter(ownProducts, (product) => product.archivedAt);
  const activeOwnProducts = useArrayFilter(ownProducts, (product) => !product.archivedAt);

  const productsByID = computed(() =>
    Object.fromEntries(ownProducts.value.map((product) => [product.id, product])),
  );

  return {
    ...query,
    query,
    ownProducts,
    archivedOwnProducts,
    activeOwnProducts,
    productsByID,
  };
}

export function useOrganisationProducts(
  organisationID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [ORGANISATION_PRODUCTS_QUERY, toRef(organisationID)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getOrganisationProducts(queryKey[1]);
        return res.data;
      },
    }),
  );

  const products = computed(() => query.data.value ?? []);
  const archivedProducts = useArrayFilter(products, (product) => product.archivedAt);
  const activeProducts = useArrayFilter(products, (product) => !product.archivedAt);

  const productsByID = computed(() =>
    Object.fromEntries(products.value.map((product) => [product.id, product])),
  );

  return {
    ...query,
    products,
    archivedProducts,
    activeProducts,
    productsByID,
  };
}

export function useClientOrSupplierProducts(
  type: MaybeRefOrGetter<'supplier' | 'client'>,
  supplierId: MaybeRefOrGetter<string>,
  options?: {
    enabled?: () => boolean | undefined;
  },
) {
  const supplierQuery = useOrganisationProducts(supplierId, {
    enabled: toValue(type) === 'supplier' && (!options?.enabled || options.enabled() === true),
  });

  const clientQuery = useOwnProducts({
    enabled: toValue(type) === 'client' && (!options?.enabled || options.enabled() === true),
  });

  const products = computed(() =>
    toValue(type) === 'client' ? clientQuery.ownProducts.value : supplierQuery.products.value,
  );

  const productsByID = computed(() =>
    toValue(type) === 'client' ? clientQuery.productsByID.value : supplierQuery.productsByID.value,
  );

  const isLoading = computed(() =>
    toValue(type) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(type) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const refetch = () => {
    if (toValue(type) === 'client') {
      return clientQuery.refetch();
    }
    return supplierQuery.refetch();
  };

  return {
    queries: [supplierQuery, clientQuery],
    products,
    productsByID,
    isLoading,
    isError,
    refetch,
  };
}

export function useSupplierAssociatedProductIDs(
  supplierID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(supplierID)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getSupplierAssociatedProductIDs(queryKey[1]);
        return res.data;
      },
    }),
  );

  const associatedProductIDs = computed(() => query.data?.value ?? []);

  return {
    associatedProductIDs,
    ...query,
  };
}

export function useClientAssociatedProductIDs(
  clientID: MaybeRefOrGetter<string>,
  options?: QueryOptions,
) {
  const query = useQuery(
    mergeQueryOptions(options, {
      queryKey: [CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(clientID)] as const,
      async queryFn({ queryKey }) {
        const res = await api.getClientAssociatedProductIDs(queryKey[1]);
        return res.data;
      },
    }),
  );

  const associatedProductIDs = computed(() => query.data?.value ?? []);

  return {
    associatedProductIDs,
    ...query,
  };
}

export function useSupplierOrClientAssociatedProductIDs(otherOrgId: MaybeRefOrGetter<string>) {
  const { currentMode } = useMode();

  const clientQuery = useSupplierAssociatedProductIDs(otherOrgId, {
    enabled: () => currentMode.value === 'client',
  });
  const supplierQuery = useClientAssociatedProductIDs(otherOrgId, {
    enabled: () => currentMode.value === 'supplier',
  });

  const isLoading = computed(() =>
    toValue(currentMode) === 'client' ? clientQuery.isLoading.value : supplierQuery.isLoading.value,
  );

  const isError = computed(() =>
    toValue(currentMode) === 'client' ? clientQuery.isError.value : supplierQuery.isError.value,
  );

  const associatedProductIDs = computed(() =>
    toValue(currentMode) === 'client'
      ? clientQuery.associatedProductIDs.value
      : supplierQuery.associatedProductIDs.value,
  );

  return {
    isLoading,
    isError,
    associatedProductIDs,
  };
}

export function useCreateProduct(
  options?: MutationObserverOptions<Product, AxiosError, CreateProductRequest, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(createProductRequest) {
        const res = await api.createProduct(createProductRequest);
        return res.data;
      },
      onSuccess(data, variables) {
        queryClient.setQueryData([OWN_PRODUCTS_QUERY], (oldData: OwnProduct[] | undefined) => {
          if (!oldData) return undefined;
          return [
            { ...data, clientIDs: variables.clientIDs, answerIDs: [] },
            ...oldData,
          ] satisfies OwnProduct[];
        });
      },
    }),
  );
}

export function useUpdateProduct(
  options?: MutationObserverOptions<Product, AxiosError, EditProductRequest, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(editProductRequest) {
        const res = await api.updateProduct(editProductRequest);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([OWN_PRODUCTS_QUERY], (oldData: OwnProduct[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.map((product) =>
            product.id === data.id ? { ...product, ...data } : product,
          ) satisfies OwnProduct[];
        });
      },
    }),
  );
}

export function useArchiveProduct(
  options?: MutationObserverOptions<Product, AxiosError, string, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(productID) {
        const res = await api.archiveProduct(productID);
        return res.data;
      },
      onSuccess(data) {
        queryClient.setQueryData([OWN_PRODUCTS_QUERY], (oldData: OwnProduct[] | undefined) => {
          if (!oldData) return undefined;
          return oldData.map((product) =>
            product.id === data.id ? { ...product, ...data } : product,
          ) satisfies OwnProduct[];
        });
      },
    }),
  );
}

export function useSetSupplierAssociatedProducts(
  options?: MutationObserverOptions<
    string[],
    AxiosError,
    SetSupplierAssociatedProductsRequest,
    null
  >,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(setSupplierAssociatedProductsRequest) {
        const res = await api.setSupplierAssociatedProducts(setSupplierAssociatedProductsRequest);
        return res.data;
      },
      onSuccess(data, { supplierID }) {
        queryClient.setQueriesData(
          {
            queryKey: [SUPPLIER_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(supplierID)],
          },
          () => data,
        );
      },
    }),
  );
}

export function useSetClientAssociatedProducts(
  options?: MutationObserverOptions<string[], AxiosError, SetClientAssociatedProductsRequest, null>,
) {
  const queryClient = useQueryClient();

  return useMutation(
    mergeMutationOptions(options, {
      async mutationFn(setClientAssociatedProductsRequest) {
        const res = await api.setClientAssociatedProducts(setClientAssociatedProductsRequest);
        return res.data;
      },
      onSuccess(data, { clientID }) {
        queryClient.setQueriesData(
          {
            queryKey: [CLIENT_ASSOCIATED_PRODUCT_IDS_QUERY, toRef(clientID)],
          },
          () => data,
        );
      },
    }),
  );
}
